<template>
  <div class="create-actions">
    <slot v-bind="{ items, disabled }">
      <UIButtonIcon
        v-for="item in items"
        :key="item.label"
        v-bind="{ disabled }"
        :leading="item.icon"
        :label="item.label"
        :variant="item.variant || 'gray'"
        fill="outlined"
        size="2xl"
        @click="item.action"
      />
      <div v-if="syncing" class="create-actions__loading">
        <UIPreloader message="Your accounts are now being refreshed" />
      </div>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw } from 'vue'

import { useModalsStore } from '@/store/modals'
import { useSettingsStore } from '@/store/settings'
import { useLinkedDataConnector } from '@/hooks/linkedDataConnector'
import { useLocale } from '@/plugins/i18n'

import { connectAccount } from '@/views/LinkedData/Connectors/components/utils/helpers'

import { UIPreloader } from '@ui/core'
import { UIButtonIcon } from '@ui/buttons'

import {
  BanknotesIcon,
  HomeModernIcon,
  PaintBrushIcon,
  ScaleIcon
} from '@heroicons/vue/24/outline'
import AIIcon from '@/assets/images/icons/ai.svg'
import ArchIcon from '@/assets/images/icons/arch.svg'
import CartaIcon from '@/assets/images/icons/carta.svg'
import PrivateFundIcon from '@/assets/images/icons/private-fund.svg'
import PrivateSecurityIcon from '@/assets/images/icons/private-security.svg'

import AddAccount from '@/views/LinkedData/Connectors/components/AddAccount.vue'
import AIAgent from '@/modules/AIAgent/AIAgent.vue'
import ArchAccount from '@/modules/wizards/ArchAccount/ArchAccount.vue'
import FineArtAndCollectables from '@/modules/wizards/FineArtAndCollectables/FineArtAndCollectables.vue'
import Liability from '@/modules/wizards/Liability/Liability.vue'
import PrivateFund from '@/modules/wizards/PrivateFund/PrivateFund.vue'
import PrivateSecurityOrOther from '@/modules/wizards/PrivateSecurityOrOther/PrivateSecurityOrOther.vue'
import RealEstate from '@/modules/wizards/RealEstate/RealEstate.vue'

type Emits = {
  click: []
}

const emit = defineEmits<Emits>()

const { loading, syncing } = useLinkedDataConnector()

const modalsStore = useModalsStore()
const settingsStore = useSettingsStore()

const { t } = useLocale('layouts.Main')

const disabled = computed(() => syncing.value || loading.value)
const isAIEnabled = computed(() => settingsStore.getAIAgent)

const items = computed(() => [
  {
    icon: BanknotesIcon,
    label: 'Connect Bank, Investment & Crypto Accounts',
    action: handleClickAddAccount,
  },
  {
    icon: HomeModernIcon,
    label: 'Add Real Estate',
    action: handleClickCreateRealEstate,
  },
  {
    icon: ScaleIcon,
    label: 'Add Liability',
    action: handleClickCreateLiability,
  },
  {
    icon: PrivateFundIcon,
    label: 'Add Private Fund',
    action: handleClickCreatePrivateFund,
  },
  {
    icon: PrivateSecurityIcon,
    label: 'Add Private Security or Other',
    action: handleClickCreatePrivateSecurityOrOther,
  },
  {
    icon: PaintBrushIcon,
    label: 'Add Fine Art and Collectables',
    action: handleClickCreateFineArtAndCollectables,
  },
  {
    icon: ArchIcon,
    label: 'Add Arch Account',
    action: handleClickAddArchAccount,
  },
  {
    icon: CartaIcon,
    label: 'Add Carta Account',
    action: handleClickAddCartaAccount,
  },
  ...(isAIEnabled.value ? [{
    icon: AIIcon,
    label: t('Ask AI'),
    variant: 'secondary',
    action: handleClickAskAI
  }] : [])
])

const handleClickAddAccount = () => {
  emit('click')
  const modalInstance = modalsStore.init('add-account', markRaw(AddAccount))
  modalInstance?.open(modalsStore.getZIndex())
}

const handleClickAddArchAccount = () => {
  emit('click')
  const modalInstance = modalsStore.init('arch-account', markRaw(ArchAccount))
  modalInstance?.open(modalsStore.getZIndex())
}

const handleClickAddCartaAccount = () => {
  emit('click')
  connectAccount('carta', {
    name: 'Carta',
    institution_id: 'carta',
    logo: ArchIcon,
  }).then(({ url }) => {
    location.href = url
  })
}

const handleClickCreateRealEstate = () => {
  emit('click')
  const modalInstance = modalsStore.init('real-estate', markRaw(RealEstate))
  modalInstance?.open(modalsStore.getZIndex())
}

const handleClickCreateLiability = () => {
  emit('click')
  const modalInstance = modalsStore.init('liability', markRaw(Liability))
  modalInstance?.open(modalsStore.getZIndex())
}

const handleClickCreatePrivateFund = () => {
  emit('click')
  const modalInstance = modalsStore.init('private-fund', markRaw(PrivateFund))
  modalInstance?.open(modalsStore.getZIndex())
}

const handleClickCreatePrivateSecurityOrOther = () => {
  emit('click')
  const modalInstance = modalsStore.init(
    'private-security-or-other',
    markRaw(PrivateSecurityOrOther),
  )
  modalInstance?.open(modalsStore.getZIndex())
}

const handleClickCreateFineArtAndCollectables = () => {
  emit('click')
  const modalInstance = modalsStore.init(
    'fine-art-and-collectables',
    markRaw(FineArtAndCollectables),
  )
  modalInstance?.open(modalsStore.getZIndex())
}

const handleClickAskAI = () => {
  emit('click')
  const modalInstance = modalsStore.init('ask-ai', markRaw(AIAgent))
  modalInstance?.open(modalsStore.getZIndex())
}
</script>

<style scoped>
.create-actions {
  @apply grid grid-cols-2 sm:grid-cols-3;
  @apply gap-x-4 gap-y-6 sm:p-4;

  &__loading {
    @apply absolute inset-0;
    @apply flex items-center justify-center;
    @apply bg-white/80 dark:bg-gray-800/80;
  }
}
</style>
